<template>
  <div
    class="scrollable-chart-container"
  >
    <div
      class="scrollable-chart-item cycle-time-barge-cost"
    >
      <scatter-chart
        v-if="chartData"
        class="flex-grow"
        :data="chartData"
        :options="chartOptions"
        :plugins="chartPlugins"
      />
      <div
        v-else
        class="h-32 flex items-center justify-center"
      >
        <span class="text-grey-001">No Data Found</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Tooltip } from 'chart.js'

import ScatterChart from "@/components/scatterChart";
import {tooltipConfig} from "@/utils/chartUtils";
import {handleValidateAndFormatNumber} from "@/utils/inputFormatter";

export default {
  props: {
    data: {
      type: Array,
      default: () => []
    },
    cycleTimeTarget: {
      type: Number,
      default: 15
    }
  },
  components: {
    ScatterChart
  },
  mounted() {
    Tooltip.positioners.multi = function(elements) {
      if (!elements.length) return false

      const el = elements[0]
      const { x, y } = el.element

      return {
        x,
        y
      }
    }
  },
  watch: {
    data: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val.length) {
          const avgBargeCost = 5.2
          const avgCycleTime = Number(this.cycleTimeTarget)
          const group1 = val
            .filter((item) => item?.avg_barge_cost_per_ton_on_usd <= avgBargeCost && item?.avg_cycle_time_days <= avgCycleTime)
          const group2 = val
            .filter((item) => item?.avg_barge_cost_per_ton_on_usd > avgBargeCost && item?.avg_cycle_time_days <= avgCycleTime)
          const group3 = val
            .filter((item) => item?.avg_barge_cost_per_ton_on_usd <= avgBargeCost && item?.avg_cycle_time_days > avgCycleTime)
          const group4 = val
            .filter((item) => item?.avg_barge_cost_per_ton_on_usd > avgBargeCost && item?.avg_cycle_time_days > avgCycleTime)

          this.chartData = {
            datasets: [
              {
                data: group1.map((item) => ({
                  x: item.avg_barge_cost_per_ton_on_usd,
                  y: item.avg_cycle_time_days,
                  code: item.owner_code,
                  owner_name: item.owner_name
                })),
                backgroundColor: '#7451D0',
                pointRadius: 5
              },
              {
                data: group2.map((item) => ({
                  x: item.avg_barge_cost_per_ton_on_usd,
                  y: item.avg_cycle_time_days,
                  code: item.owner_code,
                  owner_name: item.owner_name
                })),
                backgroundColor: '#7451D0',
                pointRadius: 5
              },
              {
                data: group3.map((item) => ({
                  x: item.avg_barge_cost_per_ton_on_usd,
                  y: item.avg_cycle_time_days,
                  code: item.owner_code,
                  owner_name: item.owner_name
                })),
                backgroundColor: '#7451D0',
                pointRadius: 5
              },
              {
                data: group4.map((item) => ({
                  x: item.avg_barge_cost_per_ton_on_usd,
                  y: item.avg_cycle_time_days,
                  code: item.owner_code,
                  owner_name: item.owner_name
                })),
                backgroundColor: '#7451D0',
                pointRadius: 5
              }
            ]
          }

          this.$nextTick(() => {
            const component = document.getElementsByClassName('cycle-time-barge-cost')
            if (component.length > 0) {
              component[0].style.width = '100%'
            }
          })
        }
        else {
          this.chartData = null
        }
      }
    }
  },
  computed: {
    chartOptions() {
      if (this.data) {
        return {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              left: 25,
              top: 50,
              right: 30
            }
          },
          scales: {
            x: {
              min: 0,
              type: 'linear',
              suggestedMax: 10.4,
              grid: {
                display: false
              },
              ticks: {
                display: true,
                color: 'black',
                font: {
                  family: 'Poppins'
                },
                callback: (val) => val,
                maxRotation: 0,
                minRotation: 0,
                autoSkip: false
              }
            },
            y: {
              grid: {
                display: false
              },
              min: 0,
              max: Number(this.cycleTimeTarget) * 2,
              ticks: {
                color: 'black',
                font: {
                  family: 'Poppins'
                }
              }
            }
          },
          plugins: {
            legend: {
              display: false
            },
            chartAreaBorder: {
              borderColor: 'black',
              borderWidth: 2
            },
            customTitle: {
              y: {
                display: true,
                text: 'Cycle Time (days)',
                color: '#000000',
                font: {
                  family: 'Poppins',
                  size: 12
                }
              }
            },
            axisIcons: true,
            tooltip: tooltipConfig(
              {
                position: 'multi',
                intersect: false,
                mode: 'nearest',
                displayColors: false,
                callbacks: {
                  title(titleItem) {
                    const item = titleItem[0]
                    const { owner_name } = item?.raw
                    return owner_name
                  },
                  label(tooltipItem) {
                    const {x, y} = tooltipItem?.raw
                    const days = handleValidateAndFormatNumber(y)
                    const cost = handleValidateAndFormatNumber(x)
                    return [
                      `Time: ${days} days`,
                      `Cost: ${cost} USD/ton`
                    ]
                  }
                }
              }
            )
          }
        }
      }
      return null
    },
    chartPlugins() {
      return [
        this.colorQuadrantPlugin(),
        this.ownerNamePlugin(),
        this.chartAreaBorderPlugin(),
        this.customTitlePlugin(),
        this.axisIconPlugin(),
        this.xAxesTicksPlugin()
      ]
    }
  },
  data() {
    return {
      chartData: null
    }
  },
  methods: {
    colorQuadrantPlugin() {
      return {
        id: 'quadrantBackground',
        beforeDraw(chart) {
          const {ctx, chartArea: {left, top, right, bottom } }  = chart;

          const xScale = chart.scales.x
          const yScale = chart.scales.y
          // Vertical line exact in 5.2
          const xMid = xScale.getPixelForValue(5.2)

          // horizontal line in middle of Y axis
          const yMiddleValue = (yScale.min + yScale.max) / 2
          const yMid = yScale.getPixelForValue(yMiddleValue)

          // Quadrant background colors
          const quadrants = [
            { // Q1: Bottom-left
              x: [left, xMid],
              y: [yMid, bottom],
              color: '#D1F9E3'
            },
            { // Q2: Top-left
              x: [left, xMid],
              y: [top, yMid],
              color: '#FFD9D9'
            },
            { // Q3: Bottom-right
              x: [xMid, right],
              y: [yMid, bottom],
              color: '#FFD9D9'
            },
            { // Q4: Top-right
              x: [xMid, right],
              y: [top, yMid],
              color: '#FFD9D9'
            }
          ];

          // Draw quadrants
          quadrants.forEach(q => {
            ctx.fillStyle = q.color;
            ctx.fillRect(q.x[0], q.y[0], q.x[1] - q.x[0], q.y[1] - q.y[0]);
          });

          // Draw divider lines
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;

          // Vertical line (x-axis divider)
          ctx.beginPath();
          ctx.moveTo(xMid, top);
          ctx.lineTo(xMid, bottom);
          ctx.stroke();

          // Horizontal line (y-axis divider)
          ctx.beginPath();
          ctx.moveTo(left, yMid);
          ctx.lineTo(right, yMid);
          ctx.stroke();
        }
      }
    },
    ownerNamePlugin() {
      return {
        id: 'ownerNameLabels',
        afterDatasetsDraw(chart) {
          const {ctx, data, chartArea: {bottom}, scales: {x, y}} = chart;

          ctx.save();
          ctx.font = '600 12px Poppins';
          ctx.textAlign = 'center';
          ctx.fillStyle = '#000';

          data.datasets.forEach(dataset => {
            dataset.data.forEach(point => {
              const yPos = y.getPixelForValue(point.y) + 20;
              if (yPos < bottom) {
                ctx.fillText(point.code, x.getPixelForValue(point.x), yPos);
              }
            });
          });
          ctx.restore();
        }
      }
    },
    chartAreaBorderPlugin() {
      return {
        id: 'chartAreaBorder',
        beforeDraw(chart, args, options) {
          const {ctx, chartArea: {left, top, width, height}} = chart
          const extension = 15

          ctx.save();
          ctx.strokeStyle = options.borderColor || 'black'
          ctx.lineWidth = options.borderWidth || 2

          ctx.beginPath();
          ctx.moveTo(left, top - extension)
          ctx.lineTo(left, top + height)
          ctx.stroke()

          ctx.beginPath()
          ctx.moveTo(left, top + height)
          ctx.lineTo(left + width, top + height)
          ctx.stroke();

          ctx.restore()
        }
      }
    },
    customTitlePlugin() {
      return {
        id: 'customTitle',
        afterDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, width } } = chart

          if (options?.y?.display) {
            ctx.save()
            ctx.fillStyle = options.y.color || 'black'

            const font = options.y.font || {}
            const fontSize = font.size || 10;
            const fontFamily = font.family || 'sans-serif'
            const fontWeight = font.weight || 'normal'
            ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`

            ctx.textAlign = 'center'
            ctx.textBaseline = 'top'

            const xPos = left + 5
            const yPos = top - 40

            ctx.fillText(options.y.text, xPos, yPos)
            ctx.restore()
          }
        }
      }
    },
    axisIconPlugin() {
      return {
        id: 'axisIcons',
        afterDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, right, bottom }, scales } = chart;

          const topIcon = {
            text: '\u2BC5',
            x: left,
            y: top - 15,
            color: '#000',
            font: 'bold 20px FontAwesome'
          };

          const rightIcon = {
            text: '\u2BC8',
            x: right,
            y: bottom + 2,
            color: '#000',
            font: 'bold 20px Poppins'
          }

          ctx.save()
          ctx.font = topIcon.font
          ctx.fillStyle = topIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(topIcon.text, topIcon.x, topIcon.y)
          ctx.restore();

          ctx.save()
          ctx.font = rightIcon.font
          ctx.fillStyle = rightIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(rightIcon.text, rightIcon.x, rightIcon.y)
          ctx.restore()
        }
      }
    },
    xAxesTicksPlugin() {
      return {
        id: 'customLinearXTicksPlugin',
        afterUpdate(chart) {
          const scale = chart.scales.x

          const min = scale.min
          const max = scale.max
          const boundary = 5.2 //Because 5.2 is boundary base on card

          const middleLeft = (min + boundary) / 2
          const middleRight = (boundary + max) / 2

          scale.ticks = [
            { value: middleLeft, label: '< 5.2 USD/Ton' },
            { value: middleRight, label: '> 5.2 USD/Ton' }
          ]
        }
      }
    }
  }
}
</script>

<style lang="scss"  scoped>
.scrollable-chart-container {
  flex-grow: 1;
  display: flex;
  max-width: 100%;
  width: 100%;
  overflow-x: scroll;

  .scrollable-chart-item {
    flex: none;
    width: 1000px;
    height: 100%;
    display: flex;
  }
}
</style>
